import React from 'react';
import styled, { keyframes, css } from 'styled-components';
import { DesignSystem, getColor, getSpacing, getShadow } from './designSystem';

// Advanced Micro-Interactions & Delightful Details
// Inspired by the finest UI patterns for premium user experiences

// ============================================================================
// MAGNETIC HOVER EFFECTS - Cursor Attraction System
// ============================================================================

export const createMagneticEffect = (strength: number = 10) => css`
  transition: transform ${DesignSystem.motion.duration.fast} ${DesignSystem.motion.easing.ease};
  cursor: pointer;
  
  &:hover {
    transform: scale(1.02);
  }
  
  &:active {
    transform: scale(0.98);
  }
`;

// ============================================================================
// SOPHISTICATED RIPPLE EFFECTS
// ============================================================================

const rippleExpand = keyframes`
  from {
    transform: scale(0);
    opacity: 0.8;
  }
  to {
    transform: scale(1);
    opacity: 0;
  }
`;

export const RippleContainer = styled.div`
  position: relative;
  overflow: visible;
  padding: 8px;
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.3);
    transform: translate(-50%, -50%);
    transition: width 0.6s, height 0.6s;
    z-index: 1;
  }
  
  &:active::before {
    width: 300px;
    height: 300px;
    animation: ${rippleExpand} 0.6s ease-out;
  }
`;

// ============================================================================
// MORPHING ANIMATIONS - Shape and State Transformations
// ============================================================================

const morphButton = keyframes`
  0% { border-radius: ${DesignSystem.borderRadius.lg}; }
  50% { border-radius: ${DesignSystem.borderRadius.full}; }
  100% { border-radius: ${DesignSystem.borderRadius.lg}; }
`;

export const MorphingButton = styled.button<{ variant?: 'primary' | 'secondary'; ghost?: boolean }>`
  position: relative;
  padding: ${getSpacing('md')} ${getSpacing('xl')};
  border: none;
  background: ${props => props.ghost ? 'transparent' : (props.variant === 'secondary' 
    ? `linear-gradient(135deg, ${getColor('secondary.500')}, ${getColor('secondary.600')})`
    : `linear-gradient(135deg, ${getColor('primary.500')}, ${getColor('primary.600')})`
  )};
  color: ${props => props.ghost ? 'white' : 'white'};
  font-weight: 600;
  font-size: 1rem;
  border-radius: ${DesignSystem.borderRadius.lg};
  cursor: pointer;
  overflow: hidden;
  transition: all ${DesignSystem.motion.duration.normal} ${DesignSystem.motion.easing.bounce};
  box-shadow: ${props => props.ghost ? 'none' : getShadow('medium')};
  
  &:hover {
    transform: translateY(-2px) scale(1.02);
  }
  
  &:active {
    transform: translateY(0) scale(0.98);
  }
  
  & > * {
    position: relative;
    z-index: 2;
  }
`;

// ============================================================================
// FLOATING AND BREATHING ANIMATIONS
// ============================================================================

const breathe = keyframes`
  0%, 100% { 
    transform: scale(1) rotate(0deg);
  }
  50% { 
    transform: scale(1.02) rotate(0.5deg);
  }
`;

const floatComplex = keyframes`
  0%, 100% {
    transform: translateY(0px) rotate(0deg);
  }
  25% {
    transform: translateY(-5px) rotate(0.5deg);
  }
  50% {
    transform: translateY(-3px) rotate(0deg);
  }
  75% {
    transform: translateY(-7px) rotate(-0.5deg);
  }
`;

export const BreathingCard = styled.div<{ delay?: number }>`
  ${props => css`
    animation: ${breathe} 4s ease-in-out infinite;
    animation-delay: ${props.delay || 0}s;
  `}
  transition: all ${DesignSystem.motion.duration.normal} ${DesignSystem.motion.easing.ease};
  
  &:hover {
    animation-play-state: paused;
    transform: translateY(-8px) scale(1.03);
  }
`;

export const FloatingElement = styled.div<{ intensity?: 'subtle' | 'normal' | 'strong' }>`
  ${props => {
    const intensity = props.intensity || 'normal';
    const multiplier = intensity === 'subtle' ? 0.5 : intensity === 'strong' ? 1.5 : 1;
    
    return css`
      animation: ${floatComplex} ${3 * multiplier}s ease-in-out infinite;
      animation-delay: ${Math.random() * 2}s;
    `;
  }}
  
  &:hover {
    animation-play-state: paused;
    transform: translateY(-10px) scale(1.05);
  }
`;

// ============================================================================
// MAGNETIC ATTRACTION EFFECTS
// ============================================================================

export const MagneticWrapper = styled.div<{ strength?: number }>`
  display: inline-block;
  transition: transform ${DesignSystem.motion.duration.fast} ${DesignSystem.motion.easing.ease};
  
  &:hover {
    transform: scale(${props => 1 + (props.strength || 5) / 100});
  }
`;

// Hook for magnetic mouse follow effect
export const useMagneticEffect = (ref: React.RefObject<HTMLElement>, strength: number = 20) => {
  React.useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const handleMouseMove = (e: MouseEvent) => {
      const rect = element.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      
      const deltaX = (e.clientX - centerX) / strength;
      const deltaY = (e.clientY - centerY) / strength;
      
      element.style.transform = `translate(${deltaX}px, ${deltaY}px) scale(1.02)`;
    };

    const handleMouseLeave = () => {
      element.style.transform = 'translate(0px, 0px) scale(1)';
    };

    element.addEventListener('mousemove', handleMouseMove);
    element.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      element.removeEventListener('mousemove', handleMouseMove);
      element.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [ref, strength]);
};

// ============================================================================
// CURSOR ATTRACTION SYSTEM
// ============================================================================

const cursorGlow = keyframes`
  0%, 100% {
    box-shadow: 0 0 20px ${getColor('accent.400')}40;
  }
  50% {
    box-shadow: 0 0 30px ${getColor('accent.400')}60;
  }
`;

export const CursorAttractor = styled.div<{ attractionStrength?: number }>`
  position: relative;
  transition: all ${DesignSystem.motion.duration.fast} cubic-bezier(0.23, 1, 0.320, 1);
  
  /* No hover background box - clean hover effect only */
  &:hover {
    transform: translateY(-3px);
  }
`;

// ============================================================================
// STAGGER ANIMATION UTILITIES
// ============================================================================

export const createStaggerDelay = (index: number, baseDelay: number = 0.1) => css`
  animation-delay: ${index * baseDelay}s;
`;

// ============================================================================
// SOPHISTICATED HOVER STATES
// ============================================================================

const shimmerShift = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

export const ShimmerHover = styled.div`
  position: relative;
  background: linear-gradient(
    90deg,
    ${getColor('neutral.gray.100')} 0%,
    ${getColor('neutral.gray.200')} 50%,
    ${getColor('neutral.gray.100')} 100%
  );
  background-size: 200% 100%;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.4) 50%,
      transparent 100%
    );
    background-size: 200% 100%;
    background-position: -200% center;
    transition: background-position 0.6s ease;
    z-index: 1;
  }
  
  &:hover::before {
    animation: ${shimmerShift} 1.5s ease-in-out infinite;
  }
`;

// ============================================================================
// MICRO-INTERACTION COMPONENT LIBRARY
// ============================================================================

export const DelightfulIcon = styled.div<{ rotation?: number }>`
  display: inline-flex;
  transition: all ${DesignSystem.motion.duration.normal} ${DesignSystem.motion.easing.bounce};
  
  &:hover {
    transform: rotate(${props => props.rotation || 15}deg) scale(1.1);
    filter: brightness(1.2);
  }
`;

export const PulsatingDot = styled.div<{ color?: string; size?: number }>`
  width: ${props => props.size || 8}px;
  height: ${props => props.size || 8}px;
  border-radius: 50%;
  background: ${props => props.color || getColor('accent.500')};
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    border-radius: 50%;
    background: inherit;
    opacity: 0.3;
    animation: ${css`
      @keyframes pulse-ring {
        0% { transform: scale(1); opacity: 0.3; }
        100% { transform: scale(2); opacity: 0; }
      }
      pulse-ring 2s infinite;
    `};
  }
`;

// ============================================================================
// EXPORT ALL COMPONENTS
// ============================================================================